import { isUsingFullPaymentOnly } from "../data-to-ui-adapters/data-transform-functions.js";
import { getCsrfToken } from "../state.js";
import { showSessionExpiredModal } from "../ui/modal-functions.js";

const recalculateSessionAsync = async() => {    
    try {
        const response = await fetchRequest('/checkout/calculate/');
        const responseData = await response.json();
        return normalizeBookingResponse(responseData);
    } catch(err) {
        console.warn(err);
    }
}

const fetchAvailableSubUnits = async(
    startDate, 
    endDate, 
    unitId, 
    categoryId,
    clientId
) => {
    toggleLoadingOverlay(true);
    let dataText = null;
    try {            
        const response = await fetchRequest(
            '/checkout_calendar/subUnitAvailabilityForPeriod/', 
            {
                start_date: startDate,
                end_date: endDate,
                parent_unit_id: unitId,
                category_id: categoryId,
                client_id: clientId
            }, 'get');
                    
        dataText = await response.text(); // because original used $.parseJSON
        
    } catch (err) {
        console.error(err); 
    } finally {
        toggleLoadingOverlay(false);
    }

    return JSON.parse(dataText);
}


/**
 * Fire-and-forget function 
 * @param {*} unitId 
 */
export async function fetchSetTerms() {
    try {
        const response = await fetchRequest('/checkout/terms/');
        termsData = await response.json();

        // TODO: HATE HATE HATE THIS, Refactor as soon as possible - could possibly call this on modal click
        updateTermsPanelContent(termsData);
    } catch (err) {
        console.error('Error fetching terms and conditions');
    }
}

async function setSession(actionName, data = {}) {    


    let responseData = null;
    try {
        // Make request to set unit details and return data for the unit
        const response = await fetchRequest('/checkout/set/' + actionName, data);
        
        // Checkout will throw a "request forbidden" 403 when the session has expired and a user tries to interact with it
        if (response?.status === 403 || response?.status === 500) {
            showSessionExpiredModal();
            return;
        }
        
        responseData = await response.json();
        
        // TODO: come back to this, 
        responseData = normalizeBookingResponse(responseData);

    } catch(err) {
        console.error(err);
        return;
    }
    
    return responseData;
}

/**
 * A generic fetch request function
 * 
 * @param {string} endpoint - the endpoint to call - will often correspond to a public function in Anytime 4.5's "Checkout.php"
 * @param {object} bodyObj - body of the request
 * @param {string} method - the HTTP method to use - defaults to POST
 * @returns 
 */
const fetchRequest = async(endpoint, bodyObj = {}, method = 'POST') => {
    const csrfValue = getCsrfToken();

    if (method.toUpperCase() === 'GET') {
        const urlParams = new URLSearchParams({
            ...bodyObj,
            anytime_booking_csrf: csrfValue
        });

        const url = `${endpoint}?${urlParams.toString()}`;
        const response = await fetch(url, { method: 'GET' });
        if (response.ok) notifySessionActivity();
        return response;
    }

    // We're using URL search params to emulate the old AJAX format because that's what PHP expects...
    const formData = new URLSearchParams();
    formData.append('anytime_booking_csrf', csrfValue);
    
    // Take the key-value pairs from the passed body object and append them to form data
    Object.entries(bodyObj).forEach(([key, value]) => {
        
        // PHP expects a strange formData format for party member data
        if (Array.isArray(value)) {
            value.forEach(item => {
                formData.append(`${key}[]`, item);
            });
        } else {
            formData.append(key, value);
        }
    });

    let headers = { 
        'Content-Type': 'application/x-www-form-urlencoded',
    }

    // NOTE: not awaiting and not casting to .json for flexibility reasons - not all endpoints in the PHP legacy codebase return the type data
    // TODO: check to see if this can just return JSON
    const response = await fetch(endpoint, {
        method: method,
        headers: headers,
        body: formData
    });

    if (response.ok) notifySessionActivity();
    return response;
}

/**
 * Lets the session-timeout countdown know that the PHP session's last activity time has just
 * been refreshed by a successful request, so it can reset its visual countdown to match.
 */
function notifySessionActivity() {
    window.dispatchEvent(new Event('atb:session-activity'));
}


/**
 * Merges the following properties from the response object returned by PHP session: 
 * 'costs', 'balance_info', 'booking_info.'
 * Easier to work with in booking summary
 * @param {*} response 
 * @returns merged_booking_info
 */
function normalizeBookingResponse(response) {
    if (
        !response ||
        (!response.booking_info &&
         !response.costs &&
         !response.balance_info &&
         !response.total_costs)
    ) {
        return response;
    }

    const {
        booking_info = {},
        costs = {},
        balance_info = {},
        total_costs = {},
        chargeable_party_size = 0,
        ...rest
    } = response;

    const renamedTotalCosts = {
        all_bookings_total_unformatted       : total_costs.total_unformatted,
        all_bookings_total                   : total_costs.total,
        all_bookings_total_to_pay_unformatted: total_costs.total_to_pay_unformatted,
        all_bookings_total_to_pay            : total_costs.total_to_pay,
    };

    // Check to see if deposits are available against this booking here - this is to stop us having to do this check in the UI code, which is a pain
    balance_info['full_only'] = isUsingFullPaymentOnly(
        balance_info['full_only'],
        costs['deposit_unformatted'],
        costs['total_unformatted']
    );

    return {
        ...rest,
        booking_info: {            
            ...booking_info,
            ...costs,
            ...balance_info,
            ...renamedTotalCosts,
            chargeable_party_size
        }
    };
}


// async function placeBooking(doRedirectBooking, bookingIds) {
//     await fetchRequest('/checkout/clear/true');
//     await recalculateSessionAsync();
// }

/**
 * Save booking session
 * Returns 
 * 
 * @param {*} actionName 
 * @param {*} data 
 * @returns data reflecting the current state of the user's booking
 */
async function bookingRequest(endpoint, data = {}) {    
    let responseData = null;
    try {
        // Make request to set unit details and return data for the unit
        const response = await fetchRequest(endpoint, data);
        responseData = await response.json();


        // TODO: come back to this, 
        responseData = normalizeBookingResponse(responseData);

    } catch(err) {
        console.error(err);
        return;
    }
    
    return responseData;
}


// TODO: THis function can likely be rolled into setSession with minimal fuss
async function setCustomAddon(data) {
    let customAddonData = null;
    
    try {
        const response = await fetchRequest('/checkout/set/custom_addon/', data);
        customAddonData = await response.json();
        
    } catch (err) {
        console.warn(err);
        return {
            status: 400,
            data: {
                err
            }
        }
    }

    return {
        status: 200,
        data: {
            ...customAddonData
        }
    }
}

export {
    setSession,
    setCustomAddon,
    fetchAvailableSubUnits,
    fetchRequest,
    recalculateSessionAsync,
    bookingRequest,
    normalizeBookingResponse // TODO: doing this purely to give legacy code access, this is temporary
};
